import { useCallback, useMemo } from 'react';

interface UseToggleSetProps {
  initialValues: Array<string>;
  onUpdate: (newValues: Array<string>) => void;
}

interface UseBooleanMapToggleProps<T extends Record<string, boolean>> {
  map: T;
  onUpdate: (patch: Partial<T>) => void;
}

export function useToggleSet({ initialValues, onUpdate }: UseToggleSetProps) {
  const excluded = new Set(initialValues);

  const toggle = useCallback(
    (key: string) => {
      const next = new Set(excluded);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onUpdate(Array.from(next));
    },
    [excluded, onUpdate],
  );

  const toggleAll = useCallback(
    (keys: Array<string>) => {
      const allExcluded = keys.every((k) => excluded.has(k));
      onUpdate(allExcluded ? [] : keys);
    },
    [excluded, onUpdate],
  );

  const set = useCallback(
    (keys: Array<string>) => {
      onUpdate(keys);
    },
    [onUpdate],
  );

  const clearAll = useCallback(() => {
    onUpdate([]);
  }, [onUpdate]);

  return { excluded, toggle, toggleAll, set, clearAll };
}

export function useBooleanMapToggle<T extends Record<string, boolean>>({
  map,
  onUpdate,
}: UseBooleanMapToggleProps<T>) {
  const excluded = useMemo(() => {
    const set = new Set<string>();
    for (const key in map) {
      if (map[key]) set.add(key);
    }
    return set;
  }, [map]);

  const toggle = useCallback(
    (key: string) => {
      onUpdate({ [key]: !map[key] } as any);
    },
    [map, onUpdate],
  );

  const toggleAll = useCallback(
    (keys: Array<string>) => {
      const allExcluded = keys.every((k) => map[k]);
      const newValue = !allExcluded;

      const patch: Record<string, boolean> = {};
      keys.forEach((k) => {
        patch[k] = newValue;
      });

      onUpdate(patch as any);
    },
    [map, onUpdate],
  );

  const clearAll = useCallback(() => {
    const patch: Record<string, boolean> = {};
    for (const key in map) {
      patch[key] = false;
    }
    onUpdate(patch as any);
  }, [map, onUpdate]);

  return { excluded, toggle, toggleAll, clearAll };
}
